import { useCallback, useState } from "react";

const isPlainObject = (value) => Object.prototype.toString.call(value) === "[object Object]";

const areShallowEqual = (left, right) => {
    if (left === right) return true;
    if (!isPlainObject(left) || !isPlainObject(right)) return false;

    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;

    for (let index = 0; index < leftKeys.length; index += 1) {
        const key = leftKeys[index];
        if (left[key] !== right[key]) return false;
    }

    return true;
};

const resolveNextState = (previousState, updater) => {
    const nextState = typeof updater === "function" ? updater(previousState) : updater;
    if (!isPlainObject(nextState)) return previousState;
    return areShallowEqual(previousState, nextState) ? previousState : nextState;
};

export default function usePagedFilterState(initialState = {}) {
    const [filter, setFilterState] = useState(() => ({ ...initialState }));

    const setFilter = useCallback((updater) => {
        setFilterState((previousState) => resolveNextState(previousState, updater));
    }, []);

    const patchFilter = useCallback((patch, { resetPage = false } = {}) => {
        setFilter((previousState) => {
            const resolvedPatch = typeof patch === "function" ? patch(previousState) : patch;
            if (!isPlainObject(resolvedPatch)) return previousState;

            const nextState = { ...previousState, ...resolvedPatch };
            if (resetPage) {
                nextState.page = 1;
            }
            return nextState;
        });
    }, [setFilter]);

    const setPagination = useCallback((page, limit) => {
        patchFilter((previousState) => {
            const nextPage = page || previousState.page;
            const nextLimit = limit || previousState.limit;
            return { page: nextPage, limit: nextLimit };
        });
    }, [patchFilter]);

    return {
        filter,
        setFilter,
        patchFilter,
        setPagination,
    };
}
